import React, { useEffect, useRef, useState } from 'react';

import { IconCheckFilled, IconClose, IconErrorFilled } from 'components/Icons';
import {
  ClearInputIcon,
  Container,
  EndIcon,
  InformationArea,
  Input,
  InputWrapper,
  Label,
  StartIcon,
} from 'components/Input/InputField/BaseInputField/styles';
import { BaseInputFieldProps } from 'components/Input/InputField/BaseInputField/types';
import { useField } from 'formik';
import ErrorLabel from './ErrorLabel';
import { HIDE_ICON_DELAY_MS } from './constants';
import Typography from 'components/Typography';

const BaseInputField = (props: BaseInputFieldProps) => {
  const { startIcon, endIcon, label, showValid = true, showClear = true, ...fieldProps } = props;

  // Removing `required` from props so Input field does not start out in an error state
  const { required, ...inputProps } = props;

  const [field, meta, helpers] = useField(fieldProps);

  const inputRef = useRef<HTMLInputElement>(null);
  const [currEndIcon, setCurrEndIcon] = useState(endIcon);

  // Used to keep track of when Clear Input icon is shown
  const [isClearShown, setIsClearShown] = useState(showClear && !props.disabled);

  // Used to keep track of the timeout for showing/hiding the Clear Input icon
  const timeoutRef = useRef<NodeJS.Timer>();

  // Watch for changes on error
  useEffect(() => {
    if (meta.touched && meta.error) {
      inputRef.current?.setCustomValidity(meta.error);
      setCurrEndIcon(<IconErrorFilled />);
      return;
    }

    inputRef.current?.setCustomValidity('');
    setCurrEndIcon(showValid && field.value ? <IconCheckFilled /> : endIcon);
  }, [meta.touched, meta.error, endIcon, showValid, field.value]);

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    field.onBlur(e);
    clearTimeout(timeoutRef.current);

    // Need a short delay before hiding icon, otherwise users will not be able to click it
    timeoutRef.current = setTimeout(() => {
      setIsClearShown(false);
    }, HIDE_ICON_DELAY_MS);
  };

  const onFocus = () => {
    clearTimeout(timeoutRef.current);
    setIsClearShown(showClear && !props.disabled);
  };

  return (
    <Container id='base-input-field-container'>
      <InputWrapper id='base-input-wrapper'>
        {field.value && <Label>{label}</Label>}
        {startIcon && <StartIcon>{startIcon}</StartIcon>}
        <Input
          ref={inputRef}
          {...field}
          {...meta}
          {...inputProps}
          endIcon={!!currEndIcon}
          onBlur={onBlur}
          onFocus={onFocus}
        />
        {isClearShown && field.value && (
          <ClearInputIcon
            className='clearInput'
            title='Clear'
            endIcon={!!currEndIcon}
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              helpers.setValue('');
            }}
          >
            <IconClose />
          </ClearInputIcon>
        )}
        <EndIcon>{currEndIcon}</EndIcon>
      </InputWrapper>
      {meta.error && (
        <InformationArea id='input-field-information'>
          <ErrorLabel name={field.name} />
        </InformationArea>
      )}
      {!meta.error && required && (
        <InformationArea id='input-field-information'>
          <Typography variant='body' size='xs'>
            Required
          </Typography>
        </InformationArea>
      )}
    </Container>
  );
};

export default BaseInputField;
